import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../index';
import beltProgressionService from '../../services/firebase/belt-progression.service';
import { BeltPromotion } from '../../types/belt';
import {
  normalizeMartialArtSafe,
  parseStripeCount,
  sortPromotionsByRank,
} from '../../utils/belt-helpers';

// Re-export BeltPromotion for backward compatibility
export type { BeltPromotion } from '../../types/belt';

interface BeltProgressionState {
  promotions: Record<string, BeltPromotion>;
  promotionIds: string[];
  currentBelts: Record<string, BeltPromotion>; // Current belt for each martial art
  promotionHistory: Record<string, Array<any>>; // History of promotions for each martial art
  isLoading: boolean;
  error: string | null;
}

const initialState: BeltProgressionState = {
  promotions: {},
  promotionIds: [],
  currentBelts: {},
  promotionHistory: {},
  isLoading: false,
  error: null,
};

// Async Thunks
export const fetchUserPromotions = createAsyncThunk<
  BeltPromotion[],
  string,
  { rejectValue: string }
>('beltProgression/fetchUserPromotions', async (userId, { rejectWithValue }) => {
  try {
    const promotions = await beltProgressionService.getUserBeltPromotions(userId);
    return promotions;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch belt promotions');
  }
});

export const fetchPromotionHistory = createAsyncThunk<
  { userId: string; history: Array<any> },
  string,
  { rejectValue: string }
>('beltProgression/fetchPromotionHistory', async (userId, { rejectWithValue }) => {
  try {
    const history = await beltProgressionService.getBeltPromotionHistory(userId);
    return { userId, history };
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to fetch belt promotion history');
  }
});

export const addPromotion = createAsyncThunk<
  BeltPromotion,
  Omit<BeltPromotion, 'id'>,
  { rejectValue: string }
>('beltProgression/addPromotion', async (promotionData, { rejectWithValue, dispatch }) => {
  try {
    const promotion = await beltProgressionService.addBeltPromotion(promotionData);
    // After adding a promotion, fetch the updated history with a delay to prevent loops
    setTimeout(() => {
      dispatch(fetchPromotionHistory(promotionData.userId));
    }, 500);
    return promotion;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to add belt promotion');
  }
});

export const updatePromotion = createAsyncThunk<
  BeltPromotion,
  { id: string; data: Partial<BeltPromotion> },
  { rejectValue: string }
>('beltProgression/updatePromotion', async ({ id, data }, { rejectWithValue, dispatch }) => {
  try {
    const promotion = await beltProgressionService.updateBeltPromotion(id, data);
    // After updating a promotion, fetch the updated history
    if (data.userId) {
      dispatch(fetchPromotionHistory(data.userId));
    }
    return promotion;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to update belt promotion');
  }
});

export const deletePromotion = createAsyncThunk<
  string,
  { id: string; userId: string },
  { rejectValue: string }
>('beltProgression/deletePromotion', async ({ id, userId }, { rejectWithValue, dispatch }) => {
  try {
    await beltProgressionService.deleteBeltPromotion(id, userId);
    // After deleting a promotion, fetch the updated history
    dispatch(fetchPromotionHistory(userId));
    return id;
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to delete belt promotion');
  }
});

// Helper function to calculate current belt for each martial art
const calculateCurrentBelts = (
  promotions: Record<string, BeltPromotion>
): Record<string, BeltPromotion> => {
  const currentBelts: Record<string, BeltPromotion> = {};

  // Process all promotions to find the current (most recent) belt for each martial art
  Object.values(promotions).forEach(promotion => {
    // Only consider active promotions
    if (!promotion.isActive) return;

    // D1: Normalize martial art name (handles BJJ variations, empty strings, etc.)
    const normalizedMartialArt = normalizeMartialArtSafe(promotion.martialArt, 'Brazilian Jiu Jitsu');

    // Initialize or update if this promotion is newer
    if (
      !currentBelts[normalizedMartialArt] ||
      promotion.date > currentBelts[normalizedMartialArt].date
    ) {
      currentBelts[normalizedMartialArt] = {
        ...promotion,
        martialArt: normalizedMartialArt,
        // D2: Ensure stripes is a valid number (handle legacy string/undefined data)
        stripes: parseStripeCount(promotion.stripes)
      };
    }
  });

  return currentBelts;
};

// Create the slice
const beltProgressionSlice = createSlice({
  name: 'beltProgression',
  initialState,
  reducers: {
    clearPromotions: (state) => {
      state.promotions = {};
      state.promotionIds = [];
      state.currentBelts = {};
      state.promotionHistory = {};
    },
    resetError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch user promotions
    builder.addCase(fetchUserPromotions.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchUserPromotions.fulfilled, (state, action) => {
      state.isLoading = false;
      
      // Normalize promotions
      const promotions: Record<string, BeltPromotion> = {};
      const promotionIds: string[] = [];
      
      action.payload.forEach(promotion => {
        promotions[promotion.id] = promotion;
        promotionIds.push(promotion.id);
      });
      
      state.promotions = promotions;
      state.promotionIds = promotionIds;
      
      // Calculate current belts for each martial art
      state.currentBelts = calculateCurrentBelts(promotions);
    });
    builder.addCase(fetchUserPromotions.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to fetch belt promotions';
    });
    
    // Fetch promotion history
    builder.addCase(fetchPromotionHistory.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchPromotionHistory.fulfilled, (state, action) => {
      state.isLoading = false;

      // History items should all have real Firebase IDs after migration in the service
      // Filter out any items without proper IDs (should not happen after migration)
      const history = action.payload.history
        .filter((item: any) => item.id && !item.id.startsWith('synthetic-') && !item.id.startsWith('hist-'))
        .map((item: any) => {
          const baseBelt = item.belt || 'white';
          return {
            ...item,
            date: item.date || Date.now(),
            stripes: typeof item.stripes === 'number' ? item.stripes : 0,
            martialArt: item.martialArt || 'Brazilian Jiu Jitsu',
            belt: baseBelt.toLowerCase().includes('belt') ?
              baseBelt :
              `${baseBelt.charAt(0).toUpperCase()}${baseBelt.slice(1)} Belt`
          };
        });

      // Sort history by date (oldest first)
      history.sort((a: any, b: any) => a.date - b.date);

      // Store the history by user ID for easy access
      state.promotionHistory[action.payload.userId] = history;
    });
    builder.addCase(fetchPromotionHistory.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to fetch belt promotion history';
    });
    
    // Add promotion
    builder.addCase(addPromotion.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(addPromotion.fulfilled, (state, action) => {
      state.isLoading = false;
      
      // Add the new promotion
      state.promotions[action.payload.id] = action.payload;
      state.promotionIds.push(action.payload.id);
      
      // Update current belts
      state.currentBelts = calculateCurrentBelts(state.promotions);
    });
    builder.addCase(addPromotion.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to add belt promotion';
    });
    
    // Update promotion
    builder.addCase(updatePromotion.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updatePromotion.fulfilled, (state, action) => {
      state.isLoading = false;
      
      // Update the promotion
      state.promotions[action.payload.id] = action.payload;
      
      // Update current belts
      state.currentBelts = calculateCurrentBelts(state.promotions);
    });
    builder.addCase(updatePromotion.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to update belt promotion';
    });
    
    // Delete promotion
    builder.addCase(deletePromotion.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(deletePromotion.fulfilled, (state, action) => {
      state.isLoading = false;
      
      // Remove the promotion
      const id = action.payload;
      delete state.promotions[id];
      state.promotionIds = state.promotionIds.filter(promoId => promoId !== id);
      
      // Update current belts
      state.currentBelts = calculateCurrentBelts(state.promotions);
    });
    builder.addCase(deletePromotion.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload || 'Failed to delete belt promotion';
    });
  },
});

// Export actions
export const { clearPromotions, resetError } = beltProgressionSlice.actions;

// Export selectors
export const selectBeltProgressionState = (state: RootState) => state.beltProgression;
export const selectPromotions = (state: RootState) => state.beltProgression.promotions;
export const selectPromotionIds = (state: RootState) => state.beltProgression.promotionIds;
export const selectCurrentBelts = (state: RootState) => state.beltProgression.currentBelts;
export const selectPromotionHistory = (state: RootState, userId: string) =>
  state.beltProgression.promotionHistory[userId] || [];
export const selectBeltProgressionLoading = (state: RootState) => state.beltProgression.isLoading;
export const selectBeltProgressionError = (state: RootState) => state.beltProgression.error;

// Selector for promotions sorted by belt rank (highest to lowest)
export const selectSortedPromotions = (state: RootState): BeltPromotion[] => {
  const promotions = Object.values(state.beltProgression.promotions);
  return sortPromotionsByRank(promotions);
};

// Selector for promotion history sorted by belt rank
export const selectSortedPromotionHistory = (state: RootState, userId: string): BeltPromotion[] => {
  const history = state.beltProgression.promotionHistory[userId] || [];
  return sortPromotionsByRank(history);
};

// Export the reducer
export default beltProgressionSlice.reducer;